// The Models tab's view model (SCR-153): the four harnesses, the inventory grouped by provider
// with each model's per-harness state and capability warnings, and the "applied on n of m tasks"
// line. The global allow-list lives in provider-model.ts, the agent cards in agents-model.ts. Pure and DOM-free
// (web/ui/tests/unit/models/model.test.ts); fetching and rendering live in models-tab.tsx.
import type { components } from "../../api/schema";
import { MODEL_CATALOG, modelDisplayName } from "../../primitives/model-names";
import type { ChipTone } from "../../primitives/page";
import { getPath, isObject, setPath, type Json, type JsonObject } from "../../settings/inherit";
import { compareProviders, ALL_PROVIDERS } from "../../settings/pickers";
import type { Pins } from "../../settings/pins";
import { FIELDS } from "../../settings/validate";

export type ModelInventory = components["schemas"]["InstallationModelInventory"];
export type InventoryItem = components["schemas"]["ModelInventoryItem"];
export type HarnessPolicy = components["schemas"]["InstallationSettingsV1HarnessPolicy"];
export type SettingsLayers = components["schemas"]["InstallationSettingsResponse"];
export type Invokability = InventoryItem["invokability"];
type CapabilityName = components["schemas"]["CapabilityNames"][number];

/** `key` is the settings document's name, `wire` the inventory's (camelCase) one. */
export interface Harness {
  key: "claude_code" | "codex" | "other" | "web";
  wire: "claudeCode" | "codex" | "other" | "web";
  label: string;
  blurb: string;
}

/** The agents in the order the screens show them (ADR-103: the schema's four harnesses). */
export const HARNESSES: readonly Harness[] = [
  { key: "claude_code", wire: "claudeCode", label: "Claude Code", blurb: "The claude command line and its desktop and IDE clients." },
  { key: "codex", wire: "codex", label: "Codex", blurb: "Codex and Codex desktop." },
  { key: "web", wire: "web", label: "Web chat", blurb: "The chat on the proxy's home page." },
  { key: "other", wire: "other", label: "Other tools", blurb: "Anything that is not one of the tools above." },
];

export const HARNESS_NOTE = "A harness setting chooses what each tool is offered. It is product policy, not a security control: a key's allowed models and IAM still decide what can be called.";

// ------------------------------------------------------------------------------- the policy form

export const MODEL_FIELD_SUFFIXES = ["mode", "providers", "include_new_models", "allowed", "default", "display_names"] as const;
export type ModelField = (typeof MODEL_FIELD_SUFFIXES)[number];

export function fieldPath(harness: Harness, field: ModelField): string {
  return `models.${harness.key}.${field}`;
}

/** Field path → the IaC parameter pinning it, from the pins document the proxy reported. */
export function pinsFromDocument(pins: Json | undefined, fields = FIELDS): Pins {
  const out: Record<string, string> = {};
  for (const field of fields) {
    if (field.iac !== null && getPath(pins, field.path) !== undefined) out[field.path] = field.iac;
  }
  return out;
}

export function policyOf(effective: Json | undefined, harness: Harness): HarnessPolicy {
  const raw = getPath(effective, `models.${harness.key}`);
  const policy = isObject(raw) ? raw : {};
  return {
    mode: policy["mode"] === "allow_list" ? "allow_list" : "follow_aws",
    providers: Array.isArray(policy["providers"]) ? (policy["providers"] as string[]) : [ALL_PROVIDERS],
    include_new_models: policy["include_new_models"] !== false,
    allowed: Array.isArray(policy["allowed"]) ? (policy["allowed"] as string[]) : [],
    default: typeof policy["default"] === "string" ? policy["default"] : "",
    display_names: Array.isArray(policy["display_names"]) ? (policy["display_names"] as HarnessPolicy["display_names"]) : [],
  };
}

// -------------------------------------------------------------------------------- the inventory

export interface InventoryRow {
  bedrockId: string;
  /** The family name a client sends and a policy lists (`claude-opus-5-5`). */
  publicName: string;
  /** What a person reads ("Opus 5.5"): the catalog's name for the Bedrock id, else cut from the public name. */
  displayName: string;
  invokable: boolean;
  invokability: Invokability;
  /** "Not authorized for this role" and so on; the server's `reason` is shown beside it. */
  invokabilityLabel: string;
  reason: string | null;
  capabilities: CapabilityChip[];
  priced: boolean;
  states: Record<Harness["key"], "enabled" | "available_not_enabled">;
  /** Per harness, the capabilities it needs that the model lacks or cannot be confirmed to have. */
  warnings: Record<Harness["key"], CapabilityName[]>;
  /** True when a capability on the chips is `unknown`: the catalog row is unverified. */
  unverified: boolean;
}

export interface CapabilityChip {
  name: "tools" | "streaming" | "vision" | "reasoning";
  label: string;
  state: "true" | "false" | "unknown";
  tone: ChipTone;
}

export interface InventoryGroup {
  provider: string;
  rows: InventoryRow[];
}

const INVOKABILITY_LABELS: Record<Invokability, string> = {
  invokable: "Invokable",
  not_authorized: "Not authorized for this role",
  not_entitled: "Not enabled in this AWS account",
  not_available: "Not available in this region",
  denied_by_policy: "Denied by an IAM policy",
  denied_observed: "AWS refused a call",
  not_text: "Not a text model",
  unknown: "Not checked yet",
};

export function invokabilityLabel(value: Invokability): string {
  return INVOKABILITY_LABELS[value];
}

const CAPABILITY_LABELS = { tools: "Tools", streaming: "Streaming", vision: "Vision", reasoning: "Reasoning" } as const;

function chipsOf(item: InventoryItem): CapabilityChip[] {
  return (["tools", "streaming", "vision", "reasoning"] as const).map((name) => {
    const state = item.capabilities[name];
    return { name, label: CAPABILITY_LABELS[name], state, tone: state === "true" ? "green" : state === "false" ? "grey" : "amber" };
  });
}

/** What a person reads for an inventory model: the catalog's name for its Bedrock id, else one cut from its family name. */
export function itemDisplayName(item: Pick<InventoryItem, "bedrockId" | "publicName">): string {
  return modelDisplayName(item.bedrockId in MODEL_CATALOG ? item.bedrockId : item.publicName || item.bedrockId);
}

export function inventoryRow(item: InventoryItem): InventoryRow {
  const chips = chipsOf(item);
  const warning = (harness: Harness): CapabilityName[] => item.warnings[harness.wire] ?? [];
  return {
    bedrockId: item.bedrockId,
    publicName: item.publicName,
    displayName: itemDisplayName(item),
    invokable: item.invokability === "invokable",
    invokability: item.invokability,
    invokabilityLabel: invokabilityLabel(item.invokability),
    reason: item.reason,
    capabilities: chips,
    priced: item.priced,
    states: Object.fromEntries(HARNESSES.map((h) => [h.key, item.harnesses[h.wire]])) as InventoryRow["states"],
    warnings: Object.fromEntries(HARNESSES.map((h) => [h.key, warning(h)])) as InventoryRow["warnings"],
    unverified: chips.some((c) => c.state === "unknown"),
  };
}

/** Grouped by provider (Anthropic and OpenAI first, then alphabetical), models by name. */
export function groupInventory(inventory: ModelInventory): InventoryGroup[] {
  return [...inventory.providers]
    .sort((a, b) => compareProviders(a.provider, b.provider))
    .map((group) => ({ provider: group.provider, rows: group.items.map(inventoryRow).sort((a, b) => a.publicName.localeCompare(b.publicName)) }));
}

export function inventoryIsEmpty(inventory: ModelInventory): boolean {
  return inventory.providers.every((group) => group.items.length === 0);
}

/** Whether a harness's providers cover the provider (`*` covers all; an unknown provider only `*`). */
export function providersCover(providers: readonly string[], provider: string): boolean {
  return providers.includes(ALL_PROVIDERS) || (provider !== "" && providers.includes(provider));
}

// ------------------------------------------------------------------------------- applied n of m

export interface AppliedSummary {
  applied: number;
  total: number;
  /** The tasks serving the sealed last known good instead of the API's document. */
  lkgOnly: number;
  text: string;
}

export function appliedSummary(layers: Pick<SettingsLayers, "reported" | "effectiveDigest">): AppliedSummary {
  const total = layers.reported.length;
  const applied = layers.reported.filter((row) => row.digest === layers.effectiveDigest).length;
  const lkgOnly = layers.reported.filter((row) => row.mode === "lkg_only").length;
  if (total === 0) return { applied, total, lkgOnly, text: "No task has reported yet, so nothing is confirmed as applied." };
  return { applied, total, lkgOnly, text: `Applied on ${applied} of ${total} ${total === 1 ? "task" : "tasks"}.` };
}

// -------------------------------------------------------------------------------- writing it

export const IF_MATCH_HEADER = "If-Match";

/**
 * A copy of the org default that a write may carry: `org` is the server's and `sign_in.served_by`
 * is the flip's, and the API refuses a body that names either (server_generated / staff-only).
 */
export function writable(document: JsonObject): JsonObject {
  const out: JsonObject = { ...document };
  delete out["org"];
  if (isObject(out["sign_in"])) {
    const signIn: JsonObject = { ...out["sign_in"] };
    delete signIn["served_by"];
    out["sign_in"] = signIn;
  }
  return out;
}

/** The headers of a settings write: the version last read. */
export function writeHeaders(version: number): Record<string, string> {
  return { [IF_MATCH_HEADER]: String(version) };
}

/** The `models` section of an override: only what the admin changed, so the rest keeps inheriting. */
export function overrideWithModels(override: JsonObject, models: Json | undefined): JsonObject {
  return setPath(override, "models", models);
}
